import React, { useEffect, useState } from "react";
import { Modal } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useCurrencyFormatter } from "../../../../Components/Hooks/useCurrencyFormatter";
import { getAccounts } from "../../../../slices/accounts/thunk";
import { invoiceAPI } from "../../../../helpers/api/invoice";
import { theme } from "../theme";
import { PAYMENT_TYPE_LABELS } from "../../../../common/paymentTypes";

const titleCase = (type) =>
  (type || "").replace(/_/g, " ").replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

const labelStyle = {
  display: "block",
  fontSize: 12.5,
  fontWeight: 600,
  color: "#63636f",
  marginBottom: 5,
};

const inputStyle = {
  width: "100%",
  fontSize: 13.5,
  padding: "9px 10px",
  borderRadius: 9,
  border: "1px solid #dcdce3",
  outline: "none",
  color: theme.text,
  background: "#fff",
};

const hintStyle = {
  display: "block",
  fontSize: 12,
  color: theme.textMuted,
  marginTop: 5,
};

const cancelBtnStyle = {
  fontSize: 13,
  fontWeight: 600,
  padding: "9px 15px",
  borderRadius: 9,
  border: "1px solid #dcdce3",
  background: "#fff",
  color: theme.text,
  cursor: "pointer",
};

const primaryBtnStyle = {
  fontSize: 13,
  fontWeight: 600,
  padding: "9px 15px",
  borderRadius: 9,
  border: "none",
  color: "#fff",
};

const CreateInvoiceModal = ({ isOpen, toggle, payment, contract, onCreateInvoice, loading = false, serverError = null }) => {
  const dispatch = useDispatch();
  const { formatCurrency } = useCurrencyFormatter();
  const { accounts } = useSelector((state) => state.Accounts);

  const [accountId, setAccountId] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [vats, setVats] = useState([]);
  const [vatIndex, setVatIndex] = useState("");
  const [vatsLoading, setVatsLoading] = useState(false);
  const [vatsError, setVatsError] = useState(null);

  // Load SmartBill accounts once, when the modal first needs them
  useEffect(() => {
    if (isOpen && (!accounts || accounts.length === 0)) {
      dispatch(getAccounts());
    }
  }, [isOpen, accounts, dispatch]);

  // Reset the form for the payment being invoiced
  useEffect(() => {
    if (isOpen && payment) {
      setAccountId("");
      setIssueDate(moment().format("YYYY-MM-DD"));
      setDueDate(payment.booking_date ? moment(payment.booking_date).format("YYYY-MM-DD") : "");
      setVats([]);
      setVatIndex("");
      setVatsError(null);
    }
  }, [isOpen, payment]);

  // Auto-pick the account when there's only one to choose from
  useEffect(() => {
    if (isOpen && !accountId && accounts && accounts.length === 1) {
      setAccountId(accounts[0].id);
    }
  }, [isOpen, accounts, accountId]);

  // VAT rates are looked up per SmartBill account — refetch whenever the account changes
  useEffect(() => {
    if (!isOpen || !accountId) return;
    let cancelled = false;
    setVatsLoading(true);
    setVatsError(null);
    setVats([]);
    setVatIndex("");

    invoiceAPI
      .getAccountVats()
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.vats || [];
        setVats(list);
        const matchIndex =
          payment?.vat != null
            ? list.findIndex((t) => Number(t.id) === Number(payment.vat))
            : list.findIndex((t) => Number(t.percentage) === Number(payment?.vat_percentage));
        if (matchIndex >= 0) setVatIndex(String(matchIndex));
      })
      .catch((err) => {
        if (!cancelled) setVatsError(err.message || "Failed to load VAT rates from SmartBill");
      })
      .finally(() => {
        if (!cancelled) setVatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, accountId]);

  if (!payment) return null;

  const account = (accounts || []).find((a) => String(a.id) === String(accountId));
  const client = contract?.client;
  const isLegal = client?.buyer_type === "LEGAL";
  const clientName = isLegal
    ? client?.legal_company_name || client?.name
    : `${client?.name || ""} ${client?.surname || ""}`.trim();
  const initials = clientName
    ? clientName.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")
    : "?";
  const unit = contract?.units?.[0];
  const contractLine = [
    contract?.pvc_number ? `Contract ${contract.pvc_number}` : null,
    unit ? `${unit.block}, Unit ${unit.nr}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const paymentName = PAYMENT_TYPE_LABELS[payment.type] || payment.type;
  const dueLabel = payment.booking_date ? moment(payment.booking_date).format("D MMM YYYY") : null;

  const today = moment().format("YYYY-MM-DD");
  const issueForCalc = issueDate || today;
  const dueOk = !!dueDate && moment(dueDate).isSameOrAfter(moment(issueForCalc), "day");
  const dueDays = dueDate ? moment(dueDate).diff(moment(issueForCalc), "days") : null;
  const issueHint = issueDate ? `Issued ${moment(issueDate).format("D MMM YYYY")}` : `Defaults to today, ${moment().format("D MMM YYYY")}`;
  const dueHint = !dueDate ? "Required" : dueOk ? `Payable in ${dueDays} day${dueDays === 1 ? "" : "s"}` : "Cannot be before the issue date";
  const dueHintColor = !dueDate ? theme.textMuted : dueOk ? theme.success : theme.dangerText;
  const dueBorderColor = dueDate && !dueOk ? "#f0c9c7" : "#dcdce3";

  // Payments are always stored in EUR; the invoice itself is issued in the
  // contract's currency, so that's what "the final amount" has to reflect.
  const invoiceCurrency = contract?.currency === "RON" ? "RON" : "EUR";
  const ronRateMissing = invoiceCurrency === "RON" && payment.amount_ron == null;
  const netAmount = invoiceCurrency === "RON" ? payment.amount_ron : payment.amount;
  const otherCurrency = invoiceCurrency === "RON" ? "EUR" : "RON";
  const otherAmount = invoiceCurrency === "RON" ? payment.amount : payment.amount_ron;

  const selectedVat = vatIndex !== "" ? vats[Number(vatIndex)] : null;
  const vatAmount = selectedVat && netAmount != null ? (netAmount * Number(selectedVat.percentage)) / 100 : null;
  const grossTotal = selectedVat && netAmount != null ? netAmount + vatAmount : null;

  const canSubmit = !!accountId && !!selectedVat && dueOk && !ronRateMissing && !vatsLoading && !loading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onCreateInvoice &&
      onCreateInvoice({
        contract_id: contract?.id,
        payment_id: payment.id,
        issue_date: issueForCalc,
        due_date: dueDate,
        account_selected_id: accountId,
        vat_percentage: Number(selectedVat.percentage),
        tax_name: selectedVat.name,
        invoice_description: description.trim(),
      });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered contentClassName="border-0 bg-transparent" style={{ maxWidth: 540 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 26px 70px rgba(20,20,32,0.28)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 16,
            padding: "20px 22px 16px",
            borderBottom: "1px solid #f0f0f4",
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 650, letterSpacing: "-0.2px", color: theme.text }}>Create invoice</div>
            <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 3 }}>For {paymentName}</div>
          </div>
          <button
            type="button"
            onClick={toggle}
            aria-label="Close"
            style={{
              flex: "none",
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid #e6e6ec",
              background: "#fff",
              color: "#63636f",
              cursor: "pointer",
            }}
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div style={{ padding: "18px 22px 20px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          {serverError && (
            <div
              style={{
                background: theme.dangerBg,
                border: `1px solid ${theme.dangerBadgeBg}`,
                color: theme.dangerText,
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 13,
              }}
            >
              {serverError}
            </div>
          )}

          {/* Client + payment card */}
          <div style={{ border: "1px solid #eeeef2", borderRadius: 11, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 14px", background: "#fafafb" }}>
              <div
                style={{
                  flex: "none",
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  background: theme.text,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 650, color: theme.text }}>{clientName || "Unknown client"}</span>
                  {client?.buyer_type_display && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: "0.4px",
                        textTransform: "uppercase",
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: theme.accentSoftBg,
                        color: theme.accent,
                      }}
                    >
                      {client.buyer_type_display}
                    </span>
                  )}
                </div>
                {contractLine && (
                  <div
                    style={{
                      fontSize: 12,
                      color: theme.textMuted,
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contractLine}
                  </div>
                )}
              </div>
              {client?.id && (
                <a href={`/client-details/${client.id}`} style={{ flex: "none", fontSize: 12.5, fontWeight: 600, color: theme.accent, whiteSpace: "nowrap" }}>
                  Client page
                </a>
              )}
            </div>

            <div
              style={{
                padding: "12px 14px",
                borderTop: "1px solid #eeeef2",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
                gap: "8px 18px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5 }}>
                <span style={{ color: theme.textMuted }}>SSN</span>
                <span style={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>{client?.ssn || "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5 }}>
                <span style={{ color: theme.textMuted }}>Email</span>
                <span style={{ fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {client?.email || "—"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5 }}>
                <span style={{ color: theme.textMuted }}>Phone</span>
                <span style={{ fontWeight: 500 }}>{client?.phone || "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5 }}>
                <span style={{ color: theme.textMuted }}>Address</span>
                <span style={{ fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {[client?.address, client?.city].filter(Boolean).join(", ") || "—"}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderTop: "1px solid #eeeef2" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: theme.text }}>{paymentName}</div>
                {dueLabel && <div style={{ fontSize: 12.5, color: theme.textMuted, marginTop: 2 }}>Due {dueLabel}</div>}
                {invoiceCurrency === "RON" && contract?.exchange_rate != null && (
                  <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 2 }}>
                    Rate: 1 EUR = {Number(contract.exchange_rate).toFixed(4)} RON
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 15, fontWeight: 650, fontVariantNumeric: "tabular-nums", color: theme.text }}>
                  {formatCurrency(netAmount, invoiceCurrency)}
                </div>
                {otherAmount != null && (
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                    {formatCurrency(otherAmount, otherCurrency)}
                  </div>
                )}
                {ronRateMissing && (
                  <div style={{ fontSize: 11.5, color: theme.dangerText, marginTop: 2, maxWidth: 160 }}>
                    EUR→RON rate unavailable
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SmartBill fields */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
            <label style={{ display: "block" }}>
              <span style={labelStyle}>SmartBill account</span>
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} style={inputStyle}>
                <option value="">Select an account</option>
                {(accounts || []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.phase_name}
                    {a.cif ? ` (CIF: ${a.cif})` : ""}
                  </option>
                ))}
              </select>
              {account?.series && <span style={hintStyle}>Series: {account.series}</span>}
            </label>

            <label style={{ display: "block" }}>
              <span style={labelStyle}>
                VAT <span style={{ color: "#a0a0ac" }}>· from SmartBill</span>
              </span>
              <select
                value={vatIndex}
                onChange={(e) => setVatIndex(e.target.value)}
                disabled={!accountId || vatsLoading || !!vatsError}
                style={inputStyle}
              >
                <option value="">
                  {!accountId ? "Select an account first" : vatsLoading ? "Loading VAT rates…" : vatsError ? "Unavailable" : "Select VAT"}
                </option>
                {vats.map((t, i) => (
                  <option key={i} value={i}>
                    {t.percentage}% {t.name ? `— ${t.name}` : ""}
                  </option>
                ))}
              </select>
              {vatsError && <span style={{ ...hintStyle, color: theme.dangerText }}>{vatsError}</span>}
            </label>

            <label style={{ display: "block" }}>
              <span style={labelStyle}>
                Issue date <span style={{ color: "#a0a0ac" }}>· optional</span>
              </span>
              <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} style={inputStyle} />
              <span style={hintStyle}>{issueHint}</span>
            </label>

            <label style={{ display: "block" }}>
              <span style={labelStyle}>Due date *</span>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{ ...inputStyle, borderColor: dueBorderColor }}
              />
              <span style={{ ...hintStyle, color: dueHintColor }}>{dueHint}</span>
            </label>
          </div>

          <label style={{ display: "block" }}>
            <span style={labelStyle}>Description on invoice</span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Down Payment - Contract PVC-684"
              style={inputStyle}
            />
          </label>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 14px",
              background: theme.accentSoftBg,
              border: `1px solid ${theme.accentSoftBorder}`,
              borderRadius: 11,
            }}
          >
            <div>
              <div style={{ fontSize: 13, color: theme.accent, fontWeight: 600 }}>Total with VAT</div>
              {selectedVat && (
                <div style={{ fontSize: 11.5, color: theme.accent, opacity: 0.8, marginTop: 2 }}>
                  {formatCurrency(netAmount, invoiceCurrency)} + {selectedVat.percentage}% VAT ({formatCurrency(vatAmount, invoiceCurrency)})
                </div>
              )}
            </div>
            <span style={{ fontSize: 16, fontWeight: 650, color: theme.accentHover, fontVariantNumeric: "tabular-nums" }}>
              {grossTotal != null ? formatCurrency(grossTotal, invoiceCurrency) : "—"}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 9,
            padding: "14px 22px",
            borderTop: "1px solid #f0f0f4",
            background: "#fafafb",
            flexShrink: 0,
          }}
        >
          <button type="button" onClick={toggle} disabled={loading} style={cancelBtnStyle}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...primaryBtnStyle,
              background: canSubmit ? theme.accent : "#c3c0e6",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {loading ? "Creating…" : "Create & issue"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateInvoiceModal;
